// TODO - Documentar
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as con from "../../GlobalConstants"
import { sum } from '../../GlobalFunctions';
import { quoteAssets } from '../../utils/marketFunctions';
import { getScore, processCustomCustomBetas } from '../../utils/orionFunctions';
import { setMarketValue, updateSpot } from '../../store/slices/marketValuesSlice';
import axios from 'axios';
import { getFinanceRequestConfig } from '../../store/logic/supportFunctions';
import { RefApplicationParameterHook } from './ApplicationParametersHooks';
import { makeSelectCloseForDate, makeSelectTRM1ForDate, makeSelectTRMForDate } from '../../store/selectors/marketValuesSelectors';

/**
 * Hook for a single market value in the store read only
 */
export function RefMarketValueHook(parameterName) {

    const value = useSelector(state => state[con.REDUCER_MARKET_VALUES][parameterName])

    return(value);
}

/**
 * Hook for the SPOT
 */
export const RefSPOT = () =>
{   
    const spot = RefMarketValueHook(con.SPOT)
    
    return spot
}


/**
 * Hook for a single Market Value in the store (read & write)
 */
export function StateMarketValueHook(parameterName) {

  const dispatch = useDispatch();

  const value = useSelector(
    state => state[con.REDUCER_MARKET_VALUES][parameterName]
  );

  const setParameter = (newVal) => {
    dispatch(
      setMarketValue({
        id: parameterName,
        value: newVal,
      })
    );
  };

  return [value, setParameter];
}


/**
 * Hook to update a single market value in the store.
 * This is write-only and dispatches a normalized payload.
 *
 * @param {string} parameterName - Key of the market value to update
 * @returns {(newVal: any) => void}
 */
export function EditMarketValueHook(parameterName) {
  const dispatch = useDispatch();

  const setParameter = (newVal) => {
    dispatch(
      setMarketValue({
        id: parameterName,
        value: newVal,
      })
    );
  };

  return setParameter;
}

/**
 * Hook for all market value in the store 
 */
 export function AllMarketValuesHook() {

    const value = useSelector(state => state[con.REDUCER_MARKET_VALUES])


    return(value);
}

/**
 * Hook for a single nested market value in the store read only
 */
 export function RefNestedMarketValueHook(parameterName, attributeName) {

    const value = useSelector(state => state[con.REDUCER_MARKET_VALUES][parameterName][attributeName])

    return(value);
}



/**
 * Hook for a loading parameter
 */
 export function RefApplicationLoadingParameterHook(loadingParameterName) {

    const value = RefNestedMarketValueHook(loadingParameterName, con.STATUS)

    return(value);
}


/**
 * Hook for a single nested market value in the store
 */
 export function SetNestedMarketValueHook(parameterName, attributeName) {

    const value = useSelector(state => state[con.REDUCER_MARKET_VALUES][parameterName])
    const setValue = (newValue) => setMarketValue(parameterName, {...value, [attributeName]: newValue})

    return(setValue);
}


/**
 * Hook for a single nested market value in the store
 */
 export function StateNestedMarketValueHook(parameterName, attributeName) {

    const allValues = useSelector(state => state[con.REDUCER_MARKET_VALUES][parameterName])
    const value = useSelector(state => state[con.REDUCER_MARKET_VALUES][parameterName][attributeName])

    const setValue = (newValue) => setMarketValue(parameterName, {...allValues, [attributeName]: newValue})

    return([value,setValue]);
}

export const useIntradayData = () => {

  const config = useMemo(() => getFinanceRequestConfig(), []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  /**
   * Fetch intraday dollar data from API
   */
  const fetchIntradayData = useCallback(async () => {

    setLoading(true);

    try {

      const response = await axios.get(
        `${con.rest_finance_url}${con.intraday_dollar_info}`,
        config
      );

      setData(response.data);

      // Update spot value in redux store
      dispatch(updateSpot());

    } catch (error) {

      console.error("Error fetching intraday data:", error);
      setData(null);

    } finally {

      setLoading(false);

    }

  }, [dispatch, config]);

  /**
   * Execute fetch when hook is mounted
   */
  useEffect(() => {

    fetchIntradayData();

  }, [fetchIntradayData]);

  return { data, loading, refetch: fetchIntradayData };

};


export const useAssessmentRateData = (coverageType) => {

    let referenceDate
    switch (coverageType) {
      case con.COVERAGES_FWD:
          referenceDate = RefApplicationParameterHook(con.FORWARD_ASSESSMENT_REFERENCE_DATE)
          break;
      case con.COVERAGES_OPTION:
          referenceDate = RefApplicationParameterHook(con.OPTION_ASSESSMENT_REFERENCE_DATE)
          break;
      default:
        referenceDate = RefApplicationParameterHook(con.FORWARD_ASSESSMENT_REFERENCE_DATE)
    }

    const selectTRM = useMemo(() => makeSelectTRMForDate(referenceDate), [referenceDate])
    const selectTRM1 = useMemo(() => makeSelectTRM1ForDate(referenceDate), [referenceDate])
    const selectClose = useMemo(() => makeSelectCloseForDate(referenceDate), [referenceDate])

    const trm = useSelector(selectTRM)
    const trm1 = useSelector(selectTRM1)
    const close = useSelector(selectClose)

    const today = useMemo(() => {
        const d = new Date()
        d.setHours(0, 0, 0, 0)
        return d
    }, [])

    const parsedReferenceDate = useMemo(() => 
        referenceDate ? new Date(referenceDate) : null
    , [referenceDate])

    const isToday = useMemo(() => 
        parsedReferenceDate?.toDateString() === today.toDateString()
    , [parsedReferenceDate, today])

    const isYesterday = useMemo(() => {
        const yesterday = new Date(today)
        yesterday.setDate(today.getDate() - 1)
        return parsedReferenceDate?.toDateString() === yesterday.toDateString()
    }, [parsedReferenceDate, today])

    return { trm, trm1, close, isToday, isYesterday }
}


/**
 * Hook for the orion summary
 */
export function RefOrionSummary() {

    const value = RefMarketValueHook(con.ORION_SUMMARY)

    return(value);
}

/**
 * Hook for the Orion real time value 
 */
 export function RefOrionRealTimeValueHook() {

    // Betas
    const orionBetas = RefMarketValueHook(con.ORION_BETAS)

    // Spot
    const spot = RefSPOT()

    // Orion value
    const [orionValue, setOrionValue] = useState(() => null)


    useEffect(() => {
     
        const updateBetas = async () =>
        {
            if(orionBetas !== null)
            {
                const report = orionBetas[con.ORION_REPORT]

                // Assets
                const assetsCodes = Object.keys(report[con.ORION_BETAS_VALUES])
                const assetConstantValue = assetsCodes.map(k => report[con.ORION_BETAS_VALUES][k])

                // Gets the codes
                const assetVariableValue = await quoteAssets(assetsCodes)

                if(assetsCodes === null)
                {
                    console.warn("Asset quoting is down. Please try again later")
                    return
                }

                
                // Computes the beta summand
                const betasSummand =  sum(assetConstantValue.map((val, i) => val*assetVariableValue[i]))

                
                // Computes custom summand
                let customSummand = 0
                for(let k in report[con.ORION_CUSTOM_BETAS]) 
                {
                    let customBetaVal = await processCustomCustomBetas(k)
                    
                    if(customBetaVal === null)
                        return
                    customSummand += customBetaVal*report[con.ORION_CUSTOM_BETAS][k]
                }

                const finalValue = betasSummand + customSummand + report[con.ORION_INTERCEPT]

                // Computes score
                const score = 100*getScore(finalValue, spot, report[con.ORION_MEAN], report[con.ORION_STD])

                setOrionValue({
                    [con.ORION_ESTIMATED_RATE] : finalValue,
                    [con.ORION_SCORE] : score,
                })
                    
            }

        }

        updateBetas();

    }, [orionBetas, spot])





    return(orionValue)
    


}



